import type { InvestmentOrder } from '@/modules/Wallet/interface/invest';
import { type FC, useState } from 'react';
import { ChevronRight, EditNote } from '@eqi-investimentos/icons/web';
import { Box, Flex, Text, Button } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { CurrencyInput } from '@/components/modules/CurrencyInput';
import { useSendClickEvent } from '@/modules/Customer/http/client/mutations/useSendClickEvent';
import { useInvest } from '@/contexts/InvestContext';
import { Thumb } from '@/components/elements/Thumb';
import { IfRender } from '@/utils/jsx';

export const SendOrderAdvisorSection: FC<SendOrderAdvisorSectionProps> = ({
  handleSelectAsset,
  handleSendOrder,
}) => {
  const t = useT();
  const { selectedAsset } = useInvest();

  const { mutate: sendClickEvent } = useSendClickEvent();

  const [amountToOrder, setAmountToOrder] = useState('');

  const assetTitle =
    selectedAsset && 'title' in selectedAsset
      ? selectedAsset?.title
      : selectedAsset?.name;
  const ticker =
    selectedAsset && 'ticker' in selectedAsset
      ? ` (${selectedAsset?.ticker})`
      : '';

  const sendOrder = (type: InvestmentOrder['type']) => {
    if (!selectedAsset) return;

    handleSendOrder({ type, asset: selectedAsset, amount: amountToOrder });

    sendClickEvent({
      label: salesforceOpportunityConfig.sendClickEvent.invest.label(
        `${type === 'buy' ? 'Enviar ordem de compra' : 'Enviar ordem de venda'} - ${assetTitle}`,

        '/investir'
      ),
    });
  };

  return (
    <Flex
      id="section-send-order-advisor"
      px="2xl"
      py="2xl"
      w="full"
      gap="l"
      bgColor="background.brandLight"
      flexDir="column"
    >
      <Text size="2xl" color="content.primary">
        {t('screens.invest.sendOrderAdvisorSection.title')}
      </Text>
      <Flex gap="s" flexDir="column">
        <Text size="l" color="content.primary">
          {t('attributes.asset')}
        </Text>
        <IfRender condition={!!assetTitle}>
          <Button
            variant="outline"
            onClick={handleSelectAsset}
            leftIcon={
              <Thumb
                src={selectedAsset?.thumb ?? ''}
                width={24}
                height={24}
                fit="cover"
                alt="eqi-broker"
              />
            }
            rightIcon={<EditNote width={6} height={6} color="content.brand" />}
          >
            <Text noOfLines={1}>{`${assetTitle}${ticker}`}</Text>
          </Button>
        </IfRender>
        <IfRender condition={!assetTitle}>
          <Button
            variant="outline"
            onClick={handleSelectAsset}
            rightIcon={
              <ChevronRight width={16} height={16} color="content.brand" />
            }
          >
            {t('screens.invest.sendOrderAdvisorSection.selectAsset')}
          </Button>
        </IfRender>
      </Flex>
      <Box my="6" w="full">
        <Text size="l" color="content.primary">
          {t('attributes.value')}
        </Text>
        <CurrencyInput
          changeValueCallback={(value: string) => setAmountToOrder(value)}
        />
      </Box>
      <Text size="m" color="content.tertiary">
        {t('screens.invest.sendOrderAdvisorSection.termsToNegotiation')}
      </Text>
      <Flex w="full" gap="m" justify="space-between">
        <Button
          onClick={() => sendOrder('buy')}
          disabled={!assetTitle}
          textColor="content.alwaysLight"
          w="full"
          flexShrink={1}
        >
          {t('screens.invest.sendOrderAdvisorSection.buy')}
        </Button>
        <Button
          onClick={() => sendOrder('sell')}
          disabled={!assetTitle}
          textColor="content.alwaysLight"
          backgroundColor="alert.error"
          w="full"
          flexShrink={1}
        >
          {t('screens.invest.sendOrderAdvisorSection.sell')}
        </Button>
      </Flex>
    </Flex>
  );
};

type SendOrderAdvisorSectionProps = {
  handleSelectAsset: () => void;
  handleSendOrder: (props: InvestmentOrder) => void;
};
